import React, { useEffect, useRef, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// A frame sequence cut from what we actually ship. On the home page it mixes the
// whole catalogue; on a product page it shows only that product, so a visitor can
// see what the thing does before deciding to install it.
//
// Frames advance on their own but the arrows and the tick marks are both live —
// waiting for a carousel to come back round to the frame you wanted is a bad way
// to look at a product.
//
// The first frame is painted at full opacity on mount, so the page has something
// to show before any timer fires, and a reduced-motion visitor gets a real still.

const STUDIO_FRAMES = [
  { src: '/reel/chrono-arena-1.webp', label: 'Chrono Arena', note: 'Key art — the Egypt gate' },
  { src: '/reel/chrono-arena-2.webp', label: 'Chrono Arena', note: 'In-engine capture' },
  { src: '/reel/tvverse-1.webp', label: 'TVVerse', note: 'Live TV — 12 channels loaded' },
  { src: '/reel/radioverse-1.webp', label: 'RadioVerse', note: '50,006 stations loaded' },
  { src: '/reel/eldritch-descent-x-2.webp', label: 'Eldritch Descent X', note: 'In-engine capture — the Void Titan' },
  { src: '/reel/pressverse-1.webp', label: 'PressVerse', note: 'Dashboard — your books at a glance' },
];

const HOLD_MS = 2200;

// Our own soundtrack — a loop cut from the Eldritch Descent score. It never
// autoplays: the file is not even fetched until someone asks for it, which is why
// preload is "none". A landing page that makes noise on arrival is a landing page
// people close.
const THEME = '/audio/reel-theme.mp3';

const Reel = ({ frames, label, sound: withSound = true, ratio = 'aspect-[21/9]' }) => {
  const list = frames && frames.length ? frames : STUDIO_FRAMES;
  const many = list.length > 1;

  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [sound, setSound] = useState(false);
  const audioRef = useRef(null);

  const toggleSound = () => {
    const el = audioRef.current;
    if (!el) return;
    if (sound) { el.pause(); setSound(false); return; }
    // The track is normalised to -14 LUFS, the usual level for web audio. It was
    // cut at -20 and played at 0.35, which averaged about -27 dB — audible only if
    // you went looking for it. This keeps it a background bed, not a soundtrack.
    el.volume = 0.55;
    el.play().then(() => setSound(true)).catch(() => setSound(false));
  };

  useEffect(() => {
    if (!many || paused) return undefined;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % list.length), HOLD_MS);
    return () => clearInterval(t);
  }, [paused, many, list.length]);
  const go = (d) => setI((n) => (n + d + list.length) % list.length);

  const navBtn =
    'pointer-events-auto flex h-11 w-11 items-center justify-center border border-border ' +
    'bg-background/70 text-primary backdrop-blur transition-colors hover:border-ember hover:bg-ember hover:text-white';

  return (
    <figure
      className="relative m-0 overflow-hidden border border-border bg-card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={`relative w-full ${ratio}`}>
        {list.map((f, n) => (
          <img
            key={f.src}
            src={f.src}
            alt={`${f.label || label || ''} — ${f.note}`}
            // Every frame loads up front. Lazy-loading them meant a frame could take
            // its turn before it had arrived, leaving the new caption sitting over the
            // previous picture. The whole set is well under half a megabyte.
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out"
            style={{ opacity: n === i ? 1 : 0 }}
          />
        ))}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-background via-background/70 to-transparent" />

        <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-5 sm:p-7">
          {/* The caption cross-fades on the same curve as the picture. Switching it
              instantly meant that for the 500ms of every fade it named a product
              the viewer was not looking at yet. */}
          <div className="relative min-w-0 flex-1">
            {list.map((f, n) => (
              <div
                key={f.src}
                aria-hidden={n !== i}
                className={`transition-opacity duration-500 ease-out ${n === i ? '' : 'absolute inset-0'}`}
                style={{ opacity: n === i ? 1 : 0 }}
              >
                <div className="label truncate text-ember-bright">{f.label || label}</div>
                <div className="mt-1 truncate font-mono text-xs text-muted">{f.note}</div>
              </div>
            ))}
          </div>
          {many && (
            <div className="hidden font-mono text-xs text-muted tabular sm:block">
              {String(i + 1).padStart(2, '0')} / {String(list.length).padStart(2, '0')}
            </div>
          )}
        </figcaption>

        {many && (
          <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6">
            <button type="button" onClick={() => go(-1)} aria-label="Previous frame" className={navBtn}>
              <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next frame" className={navBtn}>
              <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
            </button>
          </div>
        )}

        {withSound && (
          <>
            <audio ref={audioRef} src={THEME} loop preload="none" />
            <button
              type="button"
              onClick={toggleSound}
              aria-pressed={sound}
              className="absolute right-4 top-6 flex items-center gap-2 border border-border bg-background/80 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-muted backdrop-blur transition-colors hover:border-ember/60 hover:text-primary"
            >
              <span aria-hidden="true" className={sound ? 'text-ember-bright' : ''}>
                {sound ? '▮▮▮' : '▮──'}
              </span>
              {sound ? 'Sound on' : 'Sound off'}
            </button>
          </>
        )}

        {many && (
          <div className="absolute inset-x-0 top-0 flex gap-px">
            {list.map((f, n) => (
              <button
                type="button"
                key={f.src}
                onClick={() => setI(n)}
                aria-label={`Frame ${n + 1}`}
                className={`h-[3px] flex-1 transition-colors duration-500 ${
                  n === i ? 'bg-ember' : 'bg-border hover:bg-muted'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </figure>
  );
};

export default Reel;
